import { Link } from '@inertiajs/react'
import { AdminShell } from '@/components/admin/AdminShell'
import { SectionHeader } from '@/components/admin/SectionHeader'
import { AdminTable, type Column } from '@/components/admin/AdminTable'
import { Fact } from '@/components/admin/Fact'
import { adminSectionHref, visitAdminSection } from '@/components/admin/adminSections'
import { AdminBreadcrumb } from '@/components/admin/user/AdminBreadcrumb'
import { shortDate, sinceLabel } from '@/components/admin/user/adminDates'
import { focusRing } from '@/components/ui/buttonStyles'
import { fetchAdminUser } from '@/api/admin'
import { useResource } from '@/hooks/useResource'
import { cn } from '@/utils/cn'
import type { AdminUserLanguage, AdminUserProfile } from '@/types/admin'

interface AdminUserShowProps {
  id: number
}

/** One account: who they are, and the languages they are learning — each a door to its stats. */
export default function AdminUserShow({ id }: AdminUserShowProps) {
  const { data: user, error } = useResource(
    () => fetchAdminUser(id).then((res) => res.data.data),
    [id],
    'Could not load this user. Reload to try again.',
  )

  return (
    <AdminShell active="users" onSelect={visitAdminSection}>
      <SectionHeader
        title={user?.email ?? 'User'}
        breadcrumb={<AdminBreadcrumb trail={[{ label: 'Users', href: adminSectionHref('users') }]} />}
      />

      {error ? (
        <p role="alert" className="font-serif text-sm text-body">{error}</p>
      ) : user ? (
        <UserDetail user={user} />
      ) : (
        <UserDetailSkeleton />
      )}
    </AdminShell>
  )
}

function UserDetail({ user }: { user: AdminUserProfile }) {
  return (
    <div className="flex flex-col gap-12">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 xl:grid-cols-5">
        <Fact label="Role" value={user.role} />
        <Fact label="Verified" value={user.confirmed ? 'Yes' : 'No'} />
        <Fact label="Two-factor" value={user.otp_enabled ? 'On' : 'Off'} />
        <Fact label="Joined" value={shortDate(user.created_at)} />
        <Fact label="Last active" value={sinceLabel(user.last_active_at)} />
      </dl>

      <section aria-labelledby="user-languages-title">
        <h3 id="user-languages-title" className="mb-4 font-serif text-lg text-heading">
          Enrolled languages
        </h3>
        {user.languages.length === 0 ? (
          <p className="font-serif text-sm text-muted">Not enrolled in any language yet.</p>
        ) : (
          <AdminTable columns={languageColumns(user.id)} data={user.languages} keyFn={(l) => l.id} />
        )}
      </section>
    </div>
  )
}

function languageColumns(userId: number): Column<AdminUserLanguage>[] {
  return [
    {
      key: 'name',
      label: 'Language',
      render: (l) => (
        <Link
          href={`/admin/users/${userId}/languages/${l.id}`}
          className={cn(
            'inline-flex min-h-11 items-center rounded font-normal text-accent transition-colors duration-200 hover:text-heading',
            focusRing,
          )}
        >
          {l.name} &rarr;
        </Link>
      ),
    },
    { key: 'words', label: 'Words', render: (l) => l.words_count.toLocaleString(), className: 'text-right' },
    { key: 'due', label: 'Due now', render: (l) => l.due_count.toLocaleString(), className: 'text-right' },
    { key: 'enrolled', label: 'Enrolled', render: (l) => shortDate(l.enrolled_at) },
  ]
}

function UserDetailSkeleton() {
  return (
    <div data-testid="user-skeleton" aria-busy="true" className="flex flex-col gap-12">
      <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="flex flex-col gap-2">
            <div className="h-3 w-16 animate-pulse rounded bg-surface-bg" />
            <div className="h-5 w-24 animate-pulse rounded bg-surface-bg" />
          </div>
        ))}
      </div>
      <div className="h-40 animate-pulse rounded-lg bg-surface-bg" />
    </div>
  )
}
